/**
 * Free Gift With Purchase — keeps one marked promotional line per earned
 * gift variant while Liquid says the cart qualifies. Cart Transform zeros
 * those lines at checkout.
 */
(() => {
  const ROOT = window.Shopify?.routes?.root || '/';
  const GWP_PROPERTY = '_gwp';
  const GWP_VALUE = 'yes';
  const SUPPRESSED_PROMOTIONS_KEY = 'theme:gwp-suppressed-promotions';
  const TAG = 'free-gift-controller';

  let controllerSequence = 0;

  const enqueueCartMutation = (() => {
    if (window.themeEnqueueCartMutation) return window.themeEnqueueCartMutation;

    let queue = Promise.resolve();
    window.themeEnqueueCartMutation = (mutation) => {
      const next = queue.then(mutation, mutation);
      queue = next.catch(() => {});
      return next;
    };

    return window.themeEnqueueCartMutation;
  })();

  const readConfig = (root) => {
    const node = root?.querySelector?.('[data-free-gift-config]');
    if (!(node instanceof HTMLScriptElement)) return null;

    try {
      return JSON.parse(node.textContent || '');
    } catch (error) {
      return null;
    }
  };

  const request = async (path, body) => {
    const response = await fetch(`${ROOT}${path}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify(body),
    });

    if (!response.ok) throw new Error('Free gift cart mutation failed');

    return response.json();
  };

  const getCart = async () => {
    const response = await fetch(`${ROOT}cart.js`, {
      headers: { Accept: 'application/json' },
    });

    if (!response.ok) throw new Error('Free gift cart request failed');

    return response.json();
  };

  const readSuppressedPromotions = () => {
    try {
      return new Set(JSON.parse(sessionStorage.getItem(SUPPRESSED_PROMOTIONS_KEY) || '[]'));
    } catch (error) {
      return new Set();
    }
  };

  const writeSuppressedPromotions = (promotions) => {
    try {
      sessionStorage.setItem(SUPPRESSED_PROMOTIONS_KEY, JSON.stringify([...promotions]));
    } catch (error) {
      // Storage can be unavailable in privacy modes; cart reconciliation still works.
    }
  };

  const suppressPromotion = (promotion) => {
    if (!promotion) return;

    const promotions = readSuppressedPromotions();
    promotions.add(promotion);
    writeSuppressedPromotions(promotions);
  };

  const campaignsFrom = (config) => {
    if (Array.isArray(config?.campaigns)) return config.campaigns;
    if (config?.giftVariantId) return [config];
    return [];
  };

  // A declined gift stays declined only while the cart still earns it. Liquid
  // emits earned campaigns and nothing else, so a promotion missing from the
  // list is one the cart has stopped earning and its suppression is spent.
  // Keying off a `qualifies: false` entry instead would never release, because
  // no such entry is ever emitted.
  const releaseUnqualifiedSuppressions = (campaigns) => {
    const earned = new Set(
      campaigns.map((campaign) => String(campaign.promotion || '')).filter(Boolean)
    );
    const promotions = readSuppressedPromotions();
    let changed = false;

    for (const promotion of promotions) {
      if (earned.has(promotion)) continue;

      promotions.delete(promotion);
      changed = true;
    }

    if (changed) writeSuppressedPromotions(promotions);
  };

  const isPromotionSuppressed = (promotion) => {
    const token = String(promotion || '');
    if (!token) return false;

    return readSuppressedPromotions().has(token);
  };

  const isCampaignSuppressed = (campaign) =>
    isPromotionSuppressed(campaign.promotion);

  const isEarnedCampaign = (campaign) =>
    campaign.active === true &&
    campaign.qualifies === true &&
    (Number(campaign.giftVariantId) || 0) > 0 &&
    !isCampaignSuppressed(campaign);

  /**
   * Whether the cart as rendered already holds the right promotional lines.
   *
   * @param {object} config
   * @returns {boolean}
   */
  const isSettled = (config) => {
    const campaigns = campaignsFrom(config);
    releaseUnqualifiedSuppressions(campaigns);

    const earned = campaigns.filter(isEarnedCampaign);
    const promoLineCount = Number(config.promoLineCount) || 0;

    if (earned.length === 0) return promoLineCount === 0;

    return (
      promoLineCount === earned.length &&
      earned.every((campaign) => Number(campaign.giftLineQuantity) === 1)
    );
  };

  /**
   * Reconciles from server-rendered section config.
   *
   * @param {ParentNode} root
   * @returns {Promise<boolean>} Whether the cart was changed
   */
  const reconcile = (root) => {
    const config = readConfig(root);
    if (!config) return Promise.resolve(false);

    return enqueueCartMutation(async () => {
      let mutated = false;

      try {
        const cart = await getCart();
        const promoLines = cart.items.filter(
          (item) => item.properties?.[GWP_PROPERTY] === GWP_VALUE
        );
        const campaigns = campaignsFrom(config);
        releaseUnqualifiedSuppressions(campaigns);

        const earned = campaigns.filter(isEarnedCampaign);
        const keeperKeys = new Set();
        const missing = [];

        for (const campaign of earned) {
          const giftVariantId = Number(campaign.giftVariantId) || 0;
          const promotion = String(campaign.promotion || '');
          const line =
            promoLines.find(
              (item) =>
                !keeperKeys.has(item.key) &&
                Number(item.variant_id) === giftVariantId &&
                String(item.properties?._gwp_promotion || '') === promotion
            ) ||
            promoLines.find(
              (item) => !keeperKeys.has(item.key) && Number(item.variant_id) === giftVariantId
            );

          if (line) {
            keeperKeys.add(line.key);
          } else {
            missing.push(campaign);
          }
        }

        // An earlier pass may have already fixed the cart this section was
        // rendered from. Nothing left to change, but the markup is still stale.
        const isStale =
          promoLines.length !== earned.length ||
          earned.some((campaign) => Number(campaign.giftLineQuantity) !== 1);

        const updates = {};

        for (const item of promoLines) {
          if (!keeperKeys.has(item.key)) updates[item.key] = 0;
        }

        for (const item of promoLines) {
          if (keeperKeys.has(item.key) && Number(item.quantity) !== 1) {
            updates[item.key] = 1;
          }
        }

        if (Object.keys(updates).length > 0) {
          await request('cart/update.js', { updates });
          mutated = true;
        }

        if (missing.length > 0) {
          await request('cart/add.js', {
            items: missing.map((campaign) => ({
              id: Number(campaign.giftVariantId) || 0,
              quantity: 1,
              properties: {
                [GWP_PROPERTY]: GWP_VALUE,
                _gwp_promotion: String(campaign.promotion || ''),
              },
            })),
          });
          mutated = true;
        }

        return mutated || isStale;
      } catch (error) {
        document.dispatchEvent(new CustomEvent('cart_error'));

        // A partial mutation still has to reach the view, where a gift the cart
        // no longer earns is rendered as an ordinary, removable line.
        return mutated;
      }
    });
  };

  class FreeGiftController extends HTMLElement {
    #host = null;
    #id = `gwp-${(controllerSequence += 1)}`;

    connectedCallback() {
      // A section refresh replaces this element mid-reconcile, and an event from
      // a detached node would reach nothing, so the cart itself is the target.
      this.#host = this.closest('cart-page, quick-cart');

      const config = readConfig(this);

      if (!config || isSettled(config)) return;

      // Emitted before the first await: checkout and the wallets unlock from the
      // cart as rendered, which is one line out of date until reconcile settles.
      this.#emit('free-gift:pending');
      this.#reconcile();
    }

    #emit(name) {
      const target = this.#host || this;

      target.dispatchEvent(
        new CustomEvent(name, {
          bubbles: true,
          // Locks are tracked per controller: a replaced one settling must not
          // release the lock its replacement is holding.
          detail: { id: this.#id },
        })
      );
    }

    async #reconcile() {
      let changed = false;

      try {
        changed = await reconcile(this);
      } finally {
        this.#emit('free-gift:settled');
      }

      // Deliberately not gated on `isConnected`: a mutation that lands after
      // this controller is replaced is exactly the one the view has missed.
      if (changed) this.#emit('free-gift:updated');
    }
  }

  window.themeFreeGift = { reconcile, suppressPromotion, isPromotionSuppressed };

  if (!customElements.get(TAG)) {
    customElements.define(TAG, FreeGiftController);
  }
})();
